import type {
  CosmicProductType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { getTerms } from '@/lib/hooks/services/useGetTerms'
import { customerTypeHasSubscriptionDiscount } from '@/lib/utils/shared/customerTypeHasSubscriptionDiscount'
import { isProductOptionSubscription } from '@/lib/utils/shared/IsProductOptionSubscription'
import type { CartItemType, CartType } from '@/store/types/cart'
import type { CustomerType } from '@/store/types/user'

export type MixpanelCartData = {
  item_sku: string
  item_name: string
  item_variant: string
  purchase_type: string
  price: number
  currency: string
  add_to_cart_location: string
  focus_mode?: boolean
}

export type MixpanelProductData = {
  item_sku: string
  item_name: string
  item_variant: string
  price: number
  currency: string
  index?: number
}

const parsePurchaseType = (purchaseType: string | null) =>
  purchaseType === 'otp' ? 'one-time-purchase' : purchaseType || ''

export const generateCartItemEventData = (
  cartItem: CartItemType,
  currency: string,
  price: number,
) => {
  const cart: MixpanelCartData[] = []

  for (let index = 1; index <= cartItem.qty; index += 1) {
    cart.push({
      item_sku: cartItem.product.sku || '',
      item_name: cartItem.title,
      item_variant: cartItem.product.combinationName || '',
      purchase_type: parsePurchaseType(cartItem.po),
      add_to_cart_location:
        (cartItem.addToCartLocation && cartItem.addToCartLocation[index - 1]) ||
        '',
      price,
      currency,
    })
  }

  return cart
}

type GenerateAddToCartEventDataParams = {
  cartItem: CartItemType
  currency: string
  price: number
  quantity: number
  addToCartLocation: string
  focus: boolean
}

export const generateAddToCartEventData = ({
  cartItem,
  currency,
  price,
  quantity,
  addToCartLocation,
  focus,
}: GenerateAddToCartEventDataParams) => {
  const cart: MixpanelCartData[] = []

  for (let index = 1; index <= quantity; index += 1) {
    cart.push({
      item_sku: cartItem.product.sku || '',
      item_name: cartItem.title,
      item_variant: cartItem.product.combinationName || '',
      purchase_type: parsePurchaseType(cartItem.po),
      add_to_cart_location: addToCartLocation,
      price,
      currency,
      focus_mode: focus,
    })
  }

  return cart
}

const getProductPricing = (
  cartItem: CartItemType,
  customerType: CustomerType,
) => {
  const isSubscription = isProductOptionSubscription({ po: cartItem.po })
  const price =
    getTerms({
      pricingData: cartItem.product.pricing,
      forCustomerType: customerType,
    })?.priceEach || 0

  if (isSubscription && customerTypeHasSubscriptionDiscount(customerType)) {
    return price - cartItem.subscriptionDiscount
  }

  return price
}

export const generateCartEventData = (
  cart: CartType,
  currency: string,
  customerType: CustomerType,
) => {
  const cartItems = [...cart.otp, ...cart.subscribe]

  const cartData: MixpanelCartData[] = []

  for (const cartItem of cartItems) {
    for (let index = 1; index <= cartItem.qty; index += 1) {
      cartData.push({
        item_sku: cartItem.product.sku || '',
        item_name: cartItem.title,
        item_variant: cartItem.product.combinationName || '',
        purchase_type: parsePurchaseType(cartItem.po),
        add_to_cart_location:
          (cartItem.addToCartLocation &&
            cartItem.addToCartLocation[index - 1]) ||
          '',
        price: getProductPricing(cartItem, customerType),
        currency,
      })
    }
  }

  return cartData
}

export const generateCartItemsEventData = (
  cartItems: CartItemType[],
  currency: string,
  customerType: CustomerType,
) => {
  const cartData: MixpanelCartData[] = []

  for (const cartItem of cartItems) {
    for (let index = 1; index <= cartItem.qty; index += 1) {
      cartData.push({
        item_sku: cartItem.product.sku || '',
        item_name: cartItem.title,
        item_variant: cartItem.product.combinationName || '',
        purchase_type: parsePurchaseType(cartItem.po),
        add_to_cart_location:
          (cartItem.addToCartLocation &&
            cartItem.addToCartLocation[index - 1]) ||
          '',
        price: getProductPricing(cartItem, customerType),
        currency,
      })
    }
  }

  return cartData
}

export const generateProductEventData = (
  product: CosmicProductType,
  selectedVariation: CosmicVariationType,
  currency: string,
  price: number,
  otherProperties?: Record<string, string | number>,
): MixpanelProductData[] => [
  {
    item_sku: selectedVariation.sku || '',
    item_name: product.name,
    item_variant: selectedVariation.combinationName || '',
    price,
    currency,
    ...otherProperties,
  },
]

export const getCartItemsCount = (cart: CartType) => {
  const cartItems = [...cart.otp, ...cart.subscribe]
  let itemsCount = 0

  for (const cartItem of cartItems) {
    itemsCount += cartItem.qty
  }

  return itemsCount
}
